import Button from '../../Button';
import React from 'react';
import { useFormikContext } from 'formik';
import {
  ConnectionTypes,
  FormikNewFormValues,
  NewAsset,
  useAssetConfig,
  useAssetTranslation,
  AssetParams,
  useAssetParams,
  AnyAssetApplication,
} from '@mss/mss-component-library';
import { testConnection } from '../services/testConnection';
import { Tooltip } from 'antd';

type Props = {
  overrideAssetParams?: Partial<AssetParams>;
  testConnectionType: ConnectionTypes;
  multiple?: boolean;
};

function TestAllButton({
  testConnectionType,
  overrideAssetParams,
  multiple,
}: Props) {
  const { t } = useAssetTranslation();
  const { assetType, assetSubType } = useAssetParams(overrideAssetParams);
  const { values, setFieldValue, errors } =
    useFormikContext<FormikNewFormValues<NewAsset>>();
  const {
    config: {
      listView: { subTypeKey },
    },
    matchingConfig: {
      type,
      form: { connect },
    },
  } = useAssetConfig(assetSubType);

  const relevantAssets = values.assets.filter(
    asset => asset.values[subTypeKey as keyof AnyAssetApplication] === type
  );
  const groupTempIds = relevantAssets.map(({ values: asset }) => asset.tempId);

  const hasAnyRelevantTestRunning = relevantAssets.some(
    x => x[`test_${testConnectionType}`].loading === true
  );
  const assetErrors = Array.isArray(errors.assets)
    ? (errors.assets as any[])
    : [];

  const hasRelevantConnectionError = assetErrors
    .filter((_, i) => groupTempIds.includes(i))
    .some(errorAsset =>
      Object.keys(errorAsset?.values ?? {}).some(key =>
        connect[testConnectionType]?.testConnection.valueKeys.includes(key)
      )
    );
  const testConnectionConfig = connect[testConnectionType]!.testConnection;
  const disabledButton =
    hasAnyRelevantTestRunning || hasRelevantConnectionError;
  return (
    <Tooltip
      trigger={['hover']}
      title={t(
        multiple
          ? 'Test all connections of this connection type.'
          : 'Test all connections.'
      )}
    >
      <Button
        className={multiple && 'mr-2 pointer-events-auto'}
        disabled={disabledButton}
        loading={hasAnyRelevantTestRunning}
        variant="primary"
        onClick={async () => {
          await Promise.all(
            relevantAssets
              .filter(x =>
                connect[testConnectionType]?.inputs
                  ?.filter(x => x.required)
                  .every(input => !!x.values[input.fieldName as keyof NewAsset])
              )
              .map(async asset => {
                const baseTestFieldName = `assets.${asset.values.tempId}.test_${testConnectionType}`;
                try {
                  setFieldValue(`${baseTestFieldName}.loading`, true);
                  const result = await testConnection({
                    ...testConnectionConfig,
                    type: testConnectionType,
                    assetParams: {
                      assetType,
                      assetSubType,
                    },
                    index: asset.values.tempId,
                    body: Object.entries(
                      values.assets[asset.values.tempId].values
                    ).reduce(
                      (acc, [key, val]: [string, any]) => {
                        const isEmpty = !!(
                          testConnectionConfig.cleanEmpty &&
                          (val == null || val === '')
                        );

                        if (
                          !isEmpty &&
                          testConnectionConfig.valueKeys.includes(key)
                        ) {
                          return { ...acc, [key]: val };
                        } else return acc;
                      },
                      {} as { [key: string]: any }
                    ),
                  });

                  setFieldValue(`${baseTestFieldName}.result`, result);
                } catch {
                  console.error('Failed connection test');
                } finally {
                  setFieldValue(`${baseTestFieldName}.loading`, false);
                }
              })
          );
        }}
      >
        {multiple && t(testConnectionType)}
        {!multiple &&
          t('Test all ((assetSubType)) connections', {
            assetSubType: t(assetSubType),
          })}
      </Button>
    </Tooltip>
  );
}

export default TestAllButton;
